
import { supabase } from '@/integrations/supabase/client';
import { v4 as uuidv4 } from 'uuid';

// Enum for device types
export enum DeviceType {
  DESKTOP = 'desktop',
  MOBILE = 'mobile',
  TABLET = 'tablet',
  UNKNOWN = 'unknown',
}

// Get or create a session ID from localStorage
const getSessionId = (): string => {
  const storageKey = 'site_tracking_session_id';
  let sessionId = localStorage.getItem(storageKey);
  
  if (!sessionId) {
    sessionId = uuidv4();
    localStorage.setItem(storageKey, sessionId);
  }
  
  return sessionId;
};

// Detect device type from user agent
export const detectDeviceType = (): DeviceType => {
  const userAgent = navigator.userAgent || '';
  
  // Simple detection logic, can be expanded for more accuracy
  if (/Mobi|Android/i.test(userAgent) && !/Tablet|iPad/i.test(userAgent)) {
    return DeviceType.MOBILE;
  } else if (/Tablet|iPad/i.test(userAgent)) {
    return DeviceType.TABLET;
  } else if (/Windows|Macintosh|Linux/i.test(userAgent)) {
    return DeviceType.DESKTOP;
  }
  
  return DeviceType.UNKNOWN;
};

// Record a page view in the database
export const recordPageView = async (pathname: string): Promise<void> => {
  try {
    // Get the current authenticated user, if any
    const { data: { session } } = await supabase.auth.getSession();
    const userId = session?.user?.id;
    
    // Prepare data for insertion
    const pageData = {
      page_url: window.location.href,
      pathname,
      viewer_id: userId || null,
      session_id: getSessionId(),
      user_agent: navigator.userAgent,
      referrer: document.referrer || null,
      device_type: detectDeviceType(),
      ip_address: null, // IP is best captured server-side for accuracy
    };
    
    // Record the view in the database
    await supabase.from('site_views').insert([pageData]);
    
    // Debug logging in development
    if (import.meta.env.DEV) {
      console.log('[Site Tracking] Recorded page view:', pageData);
    }
  } catch (error) {
    // Silent fail in production, log in development
    if (import.meta.env.DEV) {
      console.error('[Site Tracking] Error recording page view:', error);
    }
  }
};

// Hook to track page views as the user navigates
export const usePageViewTracking = (): void => {
  // This function will be used in main.tsx to track page views
  // with the useEffect hook and React Router's location
};
